import {Link} from '@wandb/components';
import * as globals from '@wandb/weave/common/css/globals.styles';
import {Button} from '@wandb/weave/components/Button';
import {Checkbox} from '@wandb/weave/components/Checkbox/Checkbox';
import {Tailwind} from '@wandb/weave/components/Tailwind';
import React, {memo, useCallback, useContext, useState} from 'react';

import {AccountSelectorContext} from '../../../components/Search/SearchNav/AccountSelectorContextProvider';
import {AccountPlanType} from '../../../components/Search/SearchNav/types';
import {
  useCreateInferenceSubscriptionMutation,
  useCreateSandboxesSubscriptionMutation,
  useCreateStorageSubscriptionMutation,
  useMakeOrgMembersNoAccessModelsSeatMutation,
  useUpdateCustomerDefaultPaymentMethodMutation,
  useUpgradeTrialSubscriptionMutation,
} from '../../../generated/graphql';
import {captureError} from '../../../integrations';
import {useViewer} from '../../../state/viewer/hooks';
import {useGetSubscriptionPlanByName} from '../../../util/accounts/pricing';
import {
  doNotRetryContext,
  extractErrorMessageFromApolloError,
  propagateErrorsContext,
} from '../../../util/errors';
import {privacyPolicy, termsOfService} from '../../../util/urls';
import {PlanName} from '../util';
import {NewStripeForm, type SubmitParams} from './NewStripeForm';

export type SubscribeProduct = 'storage' | 'inference' | 'sandboxes';

export type SubscribeFormProps = {
  organizationID: string;
  hasStripePaymentMethods: boolean;
  onSubscriptionCompleted: () => void;
  product?: SubscribeProduct;
};

export const PRODUCT_LABEL: Record<SubscribeProduct, string> = {
  storage: 'Storage',
  inference: 'Inference',
  sandboxes: 'Sandbox',
};

type SubscribeMutationParams = {
  organizationId?: string;
  paymentMethod: string;
};

const SubscribeFormComp = ({
  organizationID,
  hasStripePaymentMethods,
  onSubscriptionCompleted,
  product = 'storage',
}: SubscribeFormProps) => {
  const {selectedAccount} = useContext(AccountSelectorContext);
  const viewer = useViewer();
  const productLabel = PRODUCT_LABEL[product];
  const [createStorageSubscriptionMutation] =
    useCreateStorageSubscriptionMutation({
      context: {...propagateErrorsContext(), ...doNotRetryContext()},
    });

  const [createSandboxesSubscriptionMutation] =
    useCreateSandboxesSubscriptionMutation({
      context: {...propagateErrorsContext(), ...doNotRetryContext()},
    });

  const [makeOrgMembersNoAccessModelsSeatMutation] =
    useMakeOrgMembersNoAccessModelsSeatMutation({
      context: {...propagateErrorsContext(), ...doNotRetryContext()},
    });

  const [createInferenceSubscriptionMutation] =
    useCreateInferenceSubscriptionMutation({
      context: {...propagateErrorsContext(), ...doNotRetryContext()},
    });

  const {loading: plansLoading, plans} = useGetSubscriptionPlanByName();

  const communityEditionPlan = plans[PlanName.CommunityEdition];

  const [downgradeTrialSubscription] = useUpgradeTrialSubscriptionMutation({
    variables: {
      organizationID: organizationID,
      planID: communityEditionPlan?.id ?? '',
    },
  });

  const [updateDefaultPaymentMethodMutation] =
    useUpdateCustomerDefaultPaymentMethodMutation({
      context: {...propagateErrorsContext(), ...doNotRetryContext()},
    });

  const [isTermsChecked, setIsTermsChecked] = useState(false);

  const submit = useCallback(
    async ({
      stripe,
      paymentMethodID,
      setPaymentMethodAsDefault,
      setErrMsg,
    }: SubmitParams) => {
      const subscription = await subscribe();
      if (subscription == null) {
        return false;
      }

      // if the purchase was successful and the set payment method flag is true,
      // set the payment method as default
      if (
        setPaymentMethodAsDefault &&
        paymentMethodID != null &&
        organizationID != null &&
        organizationID !== ''
      ) {
        const {errors} = await updateDefaultPaymentMethodMutation({
          variables: {
            paymentMethod: paymentMethodID,
            organizationId: organizationID,
          },
        });

        // since the subscription itself was successful, just surface the error
        if (errors != null) {
          const errMsg = extractErrorMessageFromApolloError(errors);
          if (errMsg != null) {
            setErrMsg(errMsg);
          }
          return false;
        }
      }

      const paymentIntent = subscription.latest_invoice.payment_intent;
      const clientSecret = paymentIntent?.client_secret;
      const paymentIntentStatus = paymentIntent?.status;
      const needsPayment =
        ['requires_action', 'requires_payment_method'].indexOf(
          paymentIntentStatus
        ) !== -1;

      if (needsPayment) {
        const paymentConfirmed = await confirmPayment(clientSecret);
        if (!paymentConfirmed) {
          return false;
        }
      }

      onSubscriptionCompleted();
      return true;

      // HELPER FUNCTIONS

      async function subscribe(): Promise<{latest_invoice: any} | null> {
        if (paymentMethodID == null || plansLoading) {
          return null;
        }
        try {
          const variables: SubscribeMutationParams = {
            paymentMethod: paymentMethodID,
          };

          // if its a personal subscription, the org ID will be emtpy, so we don't pass it in
          if (organizationID != null && organizationID !== '') {
            variables.organizationId = organizationID;
          }

          if (
            selectedAccount?.accountPlanType === AccountPlanType.ThirtyDayTrial
          ) {
            await makeOrgMembersNoAccessModelsSeatMutation({
              variables: {organizationId: organizationID},
            });
            await downgradeTrialSubscription();
          }

          let sub: {latest_invoice: any} | null | undefined;
          switch (product) {
            case 'inference':
              sub = (await createInferenceSubscriptionMutation({variables}))
                .data?.createInferenceSubscription?.subscription;
              break;
            case 'sandboxes':
              sub = (await createSandboxesSubscriptionMutation({variables}))
                .data?.createSandboxesSubscription?.subscription;
              break;
            case 'storage':
              sub = (await createStorageSubscriptionMutation({variables})).data
                ?.createStorageSubscription?.subscription;
              break;
          }

          if (sub == null) {
            onError({
              err: "Unknown error confirming subscription. Exception didn't throw but success was not reported.",
              op: 'handleSubscribe-unknown',
              analyticsEvent: `Create ${productLabel} Subscription Error`,
              extra: {
                errorType: `unknown error confirming ${productLabel} subscription`,
              },
              userFacingErrorMsg:
                'Error confirming payment. Please wait a few moments or contact support@wandb.com for help.',
            });
            return null;
          }

          return sub;
        } catch (err) {
          const errMsg = extractErrorMessageFromApolloError(err);
          onError({
            err,
            op: 'createSubResult',
            userFacingErrorMsg: `Error processing ${productLabel} subscription payment: ${errMsg}`,
            analyticsEvent: `Create ${productLabel} Subscription Error`,
            extra: {
              reason: errMsg,
              errorType: `processing ${productLabel} subscription payment`,
            },
          });
          return null;
        }
      }

      async function confirmPayment(secret: string): Promise<boolean> {
        try {
          const result = await stripe.confirmCardPayment(secret);

          if (result.error) {
            onError({
              err: result.error.message!,
              op: 'handleCardPayment',
              analyticsEvent: `Create ${productLabel} Subscription Error`,
              extra: {
                errorType: `confirming ${productLabel} subscription payment`,
                reason: result.error.message,
              },
              userFacingErrorMsg: `We had trouble confirming your payment: ${result.error.message}`,
            });
            return false;
          }

          return true;
        } catch (err) {
          onError({
            err,
            op: 'handleCardPayment',
            extra: {
              errorType: `confirming ${productLabel} subscription payment`,
              reason: err,
            },
            analyticsEvent: `Create ${productLabel} Subscription Error`,
            userFacingErrorMsg: `We had trouble confirming your payment: ${err}`,
          });
          return false;
        }
      }

      type ErrOpts = {
        err: unknown;
        op: string;
        userFacingErrorMsg: string;
        analyticsEvent?: string;
        extra?: {[key: string]: any};
      };
      function onError(opts: ErrOpts) {
        const {err, op, userFacingErrorMsg, analyticsEvent} = opts;
        if (!(err instanceof Error) && typeof err !== 'string') {
          return;
        }

        const extra = {
          organizationID,
          userID: viewer?.id,
          units: 1,
          trial: false,
          ...opts.extra,
        };
        captureError(err, `StorageSubscribeForm-${op}`, {
          extra,
        });
        if (analyticsEvent != null) {
          window.analytics?.track(analyticsEvent, extra);
        }
        setErrMsg(userFacingErrorMsg);
      }
    },
    [
      organizationID,
      viewer?.id,
      onSubscriptionCompleted,
      updateDefaultPaymentMethodMutation,
      plansLoading,
      product,
      productLabel,
      selectedAccount?.accountPlanType,
      createInferenceSubscriptionMutation,
      createSandboxesSubscriptionMutation,
      createStorageSubscriptionMutation,
      makeOrgMembersNoAccessModelsSeatMutation,
      downgradeTrialSubscription,
    ]
  );

  if (plansLoading) {
    return null;
  }

  return (
    <Tailwind>
      <NewStripeForm
        hideOrgName
        hasStripePaymentMethods={hasStripePaymentMethods}
        orgID={organizationID}
        renderButtons={({submitWrapper, submitButtonDisabled, submitting}) => (
          <div className="mt-4 flex w-full flex-col gap-4">
            <div className="-ml-12 flex flex-row items-center">
              <Checkbox
                style={{margin: 12}}
                checked={isTermsChecked}
                onClick={() => setIsTermsChecked(!isTermsChecked)}
              />
              <span className="text-14 text-gray-700">
                I agree to the{' '}
                <Link href={termsOfService()} target="_blank">
                  terms of service
                </Link>{' '}
                and{' '}
                <Link href={privacyPolicy()} target="_blank">
                  privacy policy
                </Link>
                .
              </span>
            </div>
            <Button
              className="w-full"
              size="large"
              color={globals.THEME.accentHover}
              onClick={() => {
                window.analytics?.track(
                  `${productLabel} subscribe purchase button clicked`,
                  {organizationID, userID: viewer?.id}
                );
                submitWrapper(submit);
              }}
              disabled={submitButtonDisabled || !isTermsChecked}>
              {submitting ? 'Processing...' : 'Activate'}
            </Button>
          </div>
        )}
      />
    </Tailwind>
  );
};

export const SubscribeForm = memo(SubscribeFormComp);
